export const ASSETS = {
  TABS: {
    HARDENING: 'Hardening',
    APPLICATIONS: 'Applications',
  },
  PAGE_TITLES: {
    LAPTOPS: 'Laptops',
    SERVERS: 'Servers',
  },
  OS_NAMES: {
    MACOS: 'macOS',
    WINDOWS: 'Windows',
    LINUX: 'Linux',
    OS: 'OS',
  },
  OS_STATUS: {
    UPDATED: (osVersion: string) => `OS is up to date (${osVersion})`,
    SUPPORTED: (osVersion: string, latestVersion: string | null) =>
      latestVersion
        ? `OS is supported but not on the latest version (Current: ${osVersion},  Latest: ${latestVersion})`
        : `OS is supported but not on the latest version (${osVersion})`,
    PRE_RELEASE: (osVersion: string, latestVersion: string) =>
      `OS is on a pre-release version (${osVersion}). Outside the full security-update process. Recommended stable: ${latestVersion}.`,
    OUTDATED: (osVersion: string, latestVersion: string | null) =>
      latestVersion
        ? `OS outdated and unsupported (Current: ${osVersion}  Latest: ${latestVersion})`
        : `OS outdated and unsupported (${osVersion})`,
    CHECKING: (osVersion: string) => `Checking OS status… (${osVersion})`,
  },
  LAPTOPS: {
    TITLE: 'Laptop Inventory',
    SEARCH_PLACEHOLDER: 'Search laptops...',
    TABLE: {
      HOSTNAME: 'Endpoint',
      STATUS: 'Updates',
      OS: 'OS',
      HARDWARE: 'Hardware',
      ASSIGNED_TO: 'Owner',
      MDM_STATUS: 'Status',
      ACTIONS: 'Quick Actions',
      STATUS_INFO: {
        TITLE: 'How update status is determined',
        DESCRIPTION: [
          'Updated: the OS is on the latest stable version.',
          'Supported: the OS is still within its support lifecycle but not on the latest stable version.',
          'Outdated: the OS version is no longer supported.',
          'Checking: the OS status is still being assessed.',
        ] as const,
      },
    },
    STATUS: {
      UPDATED: 'Updated',
      SUPPORTED: 'Supported',
      OUTDATED: 'Outdated',
      CHECKING: 'Checking…',
    },
    MDM_STATUS: {
      MONITORED: 'Monitored',
      LOCKING: 'Locking…',
      LOCKED: 'Locked',
      UNLOCKING: 'Unlocking…',
      WIPE_REQUESTED: 'Wipe Requested',
      WIPING: 'Wiping…',
      WIPED: 'Wiped',
      TOOLTIP_MONITORED: 'This device is being monitored by MDM',
      TOOLTIP_LOCKING: 'Lock command queued — waiting for the device to check in',
      TOOLTIP_LOCKED: 'This device has been remotely locked by an administrator',
      TOOLTIP_UNLOCKING: 'Unlock command queued — waiting for the device to check in',
      TOOLTIP_WIPE_REQUESTED: 'A remote wipe command has been sent and is pending execution',
      TOOLTIP_WIPING: 'Wipe command queued — waiting for the device to check in',
      TOOLTIP_WIPED: 'All data on this device has been permanently erased',
    },
    SUMMARY: {
      LAPTOPS: 'laptops',
    },
    UNASSIGNED: 'Unassigned',
    DRAWER: {
      SECTIONS: {
        OVERVIEW: 'Overview',
        SYSTEM_DETAILS: 'System details',
        SECURITY: 'Security',
      },
      FIELDS: {
        OS_STATUS: 'OS Status',
        OS: 'OS',
        OS_VERSION: 'OS Version',
        ARCHITECTURE: 'Architecture',
        SERIAL_NUMBER: 'Serial NR',
        HARDWARE: 'Hardware',
        MDM_STATUS: 'MDM Status',
        EDR_AGENT: 'EDR Agent',
        LAST_SEEN: 'Last Seen',
        ASSIGNED_TO: 'Assigned to',
      },
      MDM: {
        REMOTE_WIPE: 'Remote Wipe',
        REMOTE_LOCK: 'Remote Lock',
        REMOTE_WIPE_DESCRIPTION: 'Permanently erase all data on this device. This action cannot be undone.',
        REMOTE_WIPE_CONFIRMATION_TITLE: 'Remote Wipe for',
        REMOTE_WIPE_CONFIRMATION_DESCRIPTION: 'This will permanently erase all data on this device.',

        REMOTE_WIPE_CONFIRMATION_DESCRIPTION_IRREVERSIBLE: 'This action is irreversible.',
        REMOTE_WIPE_CONFIRMATION_LABEL_PREFIX: 'Type',
        REMOTE_WIPE_CONFIRMATION_LABEL_SUFFIX: 'to confirm',
        REMOTE_WIPE_CONFIRM_BUTTON: 'Wipe Device',
        REMOTE_WIPE_CANCEL_BUTTON: 'Cancel',
        REMOTE_WIPE_SUCCESS: (hostname: string) => `Remote wipe command sent to ${hostname}`,
        REMOTE_WIPE_ERROR: (hostname: string) => `Failed to wipe ${hostname}`,
        REMOTE_WIPE_SENDING_COMMAND: 'Sending wipe command...',
        REMOTE_WIPE_WARNING: {
          TITLE: 'Remote Wipe for',
          HEADING: 'You are about to permanently erase this device.',
          ITEM_ALL_DATA_PREFIX: 'All data will be ',
          ITEM_ALL_DATA_HIGHLIGHT: 'permanently destroyed',
          ITEM_ALL_DATA_SUFFIX: '.',
          ITEM_NO_RECOVERY: 'There is no recovery option.',
          FINAL_WARNING: 'This cannot be undone. Proceed only if you are absolutely certain.',
          CANCEL_BUTTON: 'Cancel',
          CONTINUE_BUTTON: 'OK, I understand',
        },
        REMOTE_LOCK_CONFIRMATION_TITLE: 'Remote Lock for',
        REMOTE_LOCK_CONFIRMATION_DESCRIPTION: 'This will immediately lock the device.',
        REMOTE_LOCK_CONFIRMATION_DESCRIPTION_REQUIRES_PIN:
          'This will immediately lock the device, requiring a passcode to unlock.',
        REMOTE_LOCK_CONFIRM_BUTTON: 'Lock Device',
        REMOTE_LOCK_SENDING_COMMAND: 'Sending lock command...',
        REMOTE_LOCK_CANCEL_BUTTON: 'Cancel',
        REMOTE_LOCK_SUCCESS: (hostname: string) => `Remote lock command sent to ${hostname}`,
        REMOTE_LOCK_ERROR: (hostname: string) => `Failed to lock ${hostname}`,
        UNLOCK_BUTTON: 'Unlock Device',
        UNLOCK_SUCCESS: (hostname: string) => `Unlock command sent to ${hostname}`,
        UNLOCK_ERROR: (hostname: string) => `Failed to unlock ${hostname}`,
        UNLOCK_PIN_TITLE: (hostname: string) => `Unlock PIN for ${hostname}`,
        UNLOCK_PIN_DESCRIPTION: 'Enter this PIN on the device to unlock it.',
        UNLOCK_PIN_COPY_BUTTON: 'Copy PIN',
        UNLOCK_PIN_COPIED: 'PIN copied to clipboard',
        UNLOCK_PIN_CLOSE_BUTTON: 'Close',
        TOOLTIP_LOCK: 'Lock device',
        TOOLTIP_UNLOCK: 'Unlock device',
        TOOLTIP_WIPE: 'Wipe device',
      },
    },
  },
  SERVERS: {
    TITLE: 'Server Inventory',
    SEARCH_PLACEHOLDER: 'Search servers...',
    TABLE: {
      HOSTNAME: 'Endpoint',
      STATUS: 'Status',
      OS: 'OS',
      HARDWARE: 'Hardware',
      STATUS_INFO: {
        TITLE: 'How update status is determined',
        DESCRIPTION: [
          'Updated: the OS is on the latest stable version.',
          'Supported: the OS is still within its support lifecycle but not on the latest stable version.',
          'Outdated: the OS version is no longer supported.',
          'Checking: the OS status is still being assessed.',
        ] as const,
      },
    },
    SUMMARY: {
      SERVERS: 'servers',
    },
    DRAWER: {
      SECTIONS: {
        OVERVIEW: 'Overview',
        SYSTEM_DETAILS: 'System details',
        SECURITY: 'Security',
      },
      FIELDS: {
        OS_STATUS: 'OS Status',
        OS: 'OS',
        OS_VERSION: 'OS Version',
        ARCHITECTURE: 'Architecture',
        SERIAL_NUMBER: 'Serial NR',
        HARDWARE: 'Hardware',
        EDR_AGENT: 'EDR Agent',
        LAST_SEEN: 'Last Seen',
      },
    },
  },
  APPLICATIONS: {
    SEARCH_PLACEHOLDER: 'Search applications...',
    FILTER: {
      ALL: 'All',
      APPROVED: 'Approved',
      DENIED: 'Denied',
      PENDING: 'Pending Review',
    },
    TABLE: {
      HOSTNAME: 'Endpoint',
      OS: 'OS',
      TOTAL_APPS: 'Applications',
      PENDING_REVIEW: 'Pending Review',
      APPROVED: 'Approved',
      DENIED: 'Denied',
    },
    DRAWER: {
      UTILITY: 'Utility',
      TITLE: 'Applications',
      SUMMARY: 'App Status Summary',
      NO_APPS: 'No applications found',
      HIDE_UTILITIES: 'Hide utilities',
      FIELDS: {
        SOFTWARE_NAME: 'Software Name',
        VENDOR: 'Vendor',
        VERSION: 'Version',
        PLATFORM: 'Platform',
        STATUS: 'Status',
      },
      STATUS: {
        APPROVED: 'Approved',
        DENIED: 'Denied',
        PENDING_REVIEW: 'Pending Review',
      },
      SUMMARY_COUNTS: {
        TOTAL_APPS: 'applications',
        APPROVED: 'approved',
        DENIED: 'denied',
        PENDING_REVIEW: 'pending review',
      },
      EXTENDED_INFO: {
        FORMAT: 'Format',
        BUNDLE_ID: 'Bundle ID',
        LOCATION: 'Location',
      },
      STATUS_UPDATED: 'Application status updated successfully',
    },
  },
} as const
